export const CUSTOMER_KIND_LABELS = {
    person: 'Person',
    business: 'Business',
};
export const CUSTOMER_PAYER_LABELS = {
    customer_pay: 'Customer pay',
    insurance: 'Insurance',
    fleet: 'Fleet',
};
/** The digits of a phone number, without a leading US country code. */
export function phoneDigits(value) {
    const d = (value ?? '').replace(/\D/g, '');
    return d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
}
/** A phone number as people read it: (555) 318-0092 for a US number, as stored otherwise. */
export function formatCustomerPhone(value) {
    if (!value)
        return '';
    const d = phoneDigits(value);
    if (d.length === 10)
        return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    return value.trim();
}
/**
 * What the search box sends: trimmed, inner spaces collapsed. A phone number
 * typed with punctuation ("(555) 318-0092") is sent as its digits, so it
 * matches however it was stored. Blank means "no search".
 */
export function normalizeCustomerSearch(raw) {
    const t = (raw ?? '').trim().replace(/\s+/g, ' ');
    if (!t)
        return '';
    // Only digits and phone punctuation, with at least 3 digits: a phone number.
    if (/^[\d\s()+.-]+$/.test(t) && t.replace(/\D/g, '').length >= 3)
        return t.replace(/\D/g, '');
    return t;
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function looksLikeCustomerEmail(value) {
    return EMAIL_RE.test(value.trim());
}
/** A phone number has 10 digits (or 11 starting with 1) — loose, the server normalises. */
export function looksLikeCustomerPhone(value) {
    const d = value.replace(/\D/g, '');
    return d.length >= 10 && d.length <= 15 && /^[\d\s()+.-]+$/.test(value.trim());
}
/**
 * The quick-add form's one "Phone or email" box, split into what the API
 * takes. An empty box is fine (the name alone is enough); anything else must
 * read as one or the other.
 */
export function parsePhoneOrEmail(value) {
    const t = value.trim();
    if (!t)
        return {};
    if (t.includes('@'))
        return looksLikeCustomerEmail(t) ? { email: t } : null;
    return looksLikeCustomerPhone(t) ? { phone: t } : null;
}
/** Quick add: a name, plus a phone number or an email if they have one. */
export function quickAddInput(name, phoneOrEmail, kind = 'person') {
    const n = name.trim();
    if (!n)
        return { error: 'Add a name.' };
    if (n.length > 200)
        return { error: 'The name is too long.' };
    const contact = parsePhoneOrEmail(phoneOrEmail);
    if (!contact)
        return { error: 'Enter a phone number or an email.' };
    return { input: { kind, name: n, ...contact } };
}
/** The edit form's checks, before anything is sent. Returns the first problem, or null. */
export function validateCustomerInput(input) {
    const n = (input.name ?? '').trim();
    if (!n)
        return 'Add a name.';
    if (n.length > 200)
        return 'The name is too long.';
    if (input.email && input.email.trim() && !looksLikeCustomerEmail(input.email))
        return 'Check the email.';
    if (input.phone && input.phone.trim() && !looksLikeCustomerPhone(input.phone))
        return 'Check the phone number.';
    return null;
}
/** Trims every text field and drops the empty ones, so a blank box clears nothing by accident on create. */
export function cleanCustomerInput(input) {
    const out = {};
    if (input.kind)
        out.kind = input.kind;
    for (const key of ['name', 'phone', 'email', 'address', 'notes']) {
        const v = input[key]?.trim();
        if (v)
            out[key] = v;
    }
    if (input.payer_type)
        out.payer_type = input.payer_type;
    if (input.shared !== undefined)
        out.shared = input.shared;
    return out;
}
/**
 * "Already on the list?" — the visible customers with the same phone number or
 * email as the one being added, so the form can offer them before the server
 * answers 409 CUSTOMER_DUPLICATE.
 */
export function duplicateCustomerHint(candidates, input) {
    const phone = phoneDigits(input.phone);
    const email = (input.email ?? '').trim().toLowerCase();
    if (phone.length < 7 && !email)
        return [];
    return candidates.filter((c) => (phone.length >= 7 && phoneDigits(c.phone) === phone) || (!!email && (c.email ?? '').trim().toLowerCase() === email));
}
/** A vehicle in a few words: 2023 Toyota RAV4. */
export function customerVehicleTitle(v) {
    if (!v)
        return '';
    return [v.year, v.make, v.model].filter((x) => x !== undefined && x !== null && String(x).trim() !== '').join(' ');
}
/** The end of a VIN, the way the shop reads it off the windshield: …J8K41270. */
export function shortVin(vin, keep = 8) {
    if (!vin)
        return '';
    return vin.length > keep ? `…${vin.slice(-keep)}` : vin;
}
/** One line under a customer's name in a list: phone · open jobs. */
export function customerSummaryLine(c) {
    const parts = [];
    const contact = c.phone ? formatCustomerPhone(c.phone) : c.email;
    if (contact)
        parts.push(contact);
    if (c.open_jobs > 0)
        parts.push(`${c.open_jobs} open job${c.open_jobs === 1 ? '' : 's'}`);
    return parts.join(' · ');
}
/** The vehicles a customer has, most recently seen first. */
export function sortCustomerVehicles(list) {
    return [...list].sort((a, b) => (b.last_seen ?? '').localeCompare(a.last_seen ?? ''));
}
/**
 * Whether a customer can be used on a job at this store: shared ones anywhere
 * in the account, others only at the store they belong to.
 */
export function customerVisibleAt(c, locationId) {
    return c.shared || !c.location_id || c.location_id === locationId;
}
/** The store a customer belongs to, or null when shared with every store (or none given). */
export function customerHomeStore(c) {
    if (!c || c.shared || !c.location_id)
        return null;
    return c.location_id;
}
/** The edit form filled from a customer (empty for a new one: a person, this store only). */
export function customerFormFrom(c) {
    return {
        kind: c?.kind ?? 'person',
        name: c?.name ?? '',
        phone: c?.phone ?? '',
        email: c?.email ?? '',
        address: c?.address ?? '',
        notes: c?.notes ?? '',
        payer_type: c?.payer_type ?? '',
        shared: c?.shared ?? false,
    };
}
/**
 * What an edit sends: only the fields that changed. A box that was emptied (or
 * Who pays set back to Not set) is sent as "" so the server clears it.
 */
export function changedCustomerFields(before, after) {
    const out = {};
    if (after.kind !== before.kind)
        out.kind = after.kind;
    for (const k of ['name', 'phone', 'email', 'address', 'notes']) {
        if (after[k].trim() !== before[k].trim())
            out[k] = after[k].trim();
    }
    if (after.payer_type !== before.payer_type)
        out.payer_type = after.payer_type;
    if (after.shared !== before.shared)
        out.shared = after.shared;
    return out;
}
/**
 * A phone number as a dialler takes it: digits and a leading +, with any
 * extension (x, ext, #, a pause) cut off — a tel: or sms: link can't carry one.
 */
export function dialablePhone(value) {
    const t = (value ?? '').trim();
    if (!t)
        return '';
    const main = t.split(/\s*(?:ext\.?|extension|x|#|;|,|p\b|w\b)/i)[0].trim();
    const digits = main.replace(/\D/g, '');
    if (!digits)
        return '';
    return main.startsWith('+') ? `+${digits}` : digits;
}
/**
 * An email that can go into a mailto: link as it is: plain address
 * characters only, so nothing like ?, &, # or a comma can add a subject, a
 * body or another recipient.
 */
const MAILTO_SAFE_EMAIL = /^[A-Za-z0-9._%+'-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
export function mailtoSafeEmail(value) {
    const t = (value ?? '').trim();
    return MAILTO_SAFE_EMAIL.test(t) ? t : null;
}
/** Call / Text / Email for a customer, each only when the customer has that detail. */
export function customerContactActions(c) {
    const out = [];
    const phone = dialablePhone(c.phone);
    if (phone.replace('+', '').length >= 7) {
        out.push({ kind: 'call', label: 'Call', url: `tel:${phone}` });
        out.push({ kind: 'text', label: 'Text', url: `sms:${phone}` });
    }
    const email = mailtoSafeEmail(c.email);
    if (email)
        out.push({ kind: 'email', label: 'Email', url: `mailto:${email}` });
    return out;
}
